import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import {
  API_BASE_URL,
  GET_EXPENSE_TYPE,
  GET_SUPPLIERS,
  GET_USER_STORES,
} from "../../Config";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import SpinLoader from "../loader/SpinLoader";

const EditExpense = ({ show, expense, onSuccess, onClose }) => {
  const [userDataSession, setUserDataSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Form states
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState(null);
  const [billDate, setBillDate] = useState("");
  const [billAmount, setBillAmount] = useState(""); // will be read-only
  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);

  // Dropdown options
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const currentDue = Number(expense?.DueAmount || 0);
  const remainingDue = Number(expense?.DueAmount || 0);
  const isFullyPaid = remainingDue <= 0 || expense?.Status === 1;
  const [paymentHistory, setPaymentHistory] = useState([]);
  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      setUserDataSession(JSON.parse(userDataString));
    }
  }, []);

  useEffect(() => {
    if (!show || !expense || !userDataSession?.OrgId) return;

    // Pre-fill from parent expense
    setBillDate(expense.DateOfTransaction?.split("T")[0] || "");
    setBillAmount(expense.Amount?.toString() || ""); // total bill – read only
    setPaidAmount(""); // user enters new payment amount
    setPaidDate("");
    setReferenceNo(expense.ReferenceNo || "");
    setNotes("");

    setSelectedStore({
      value: expense.StoreId,
      label: expense.StoreName || "Store",
    });
    setSelectedSupplier({
      value: expense.SupplierId,
      label: expense.SupplierName || "Supplier",
    });
    setSelectedExpenseType({
      value: expense.Id,
      label: expense.Name || "Expense Type",
      isFixed: expense.IsFixed || false,
    });

    // Load real dropdowns
    const loadDropdowns = async () => {
      try {
        const [supRes, storeRes, typeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}${GET_SUPPLIERS}/${userDataSession.OrgId}`),
          axios.get(
            `${API_BASE_URL}${GET_USER_STORES}?UserId=${userDataSession.UserId}`,
          ),
          axios.get(
            `${API_BASE_URL}${GET_EXPENSE_TYPE}?OrgId=${userDataSession.OrgId}`,
          ),
        ]);

        setSuppliers(
          supRes.data?.ResultData?.map((s) => ({
            value: s.Id,
            label: s.SupplierName || "Unnamed",
          })) || [],
        );
        setStores(
          storeRes.data?.ResultData?.map((s) => ({
            value: s.Id || s.StoreId,
            label: s.Name,
          })) || [],
        );
        setExpenseTypes(
          typeRes.data?.ResultData?.map((t) => ({
            value: t.Id,
            label: t.Name,
            isFixed: t.IsFixed,
          })) || [],
        );
      } catch (err) {
        console.error("Dropdown load failed:", err);
      }
    };

    loadDropdowns();
    const fetchPaymentHistory = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}Misc/getExpenseById?OrgId=${userDataSession.OrgId}&Id=${expense.Id}`,
        );

        if (res.data?.Status && Array.isArray(res.data.ResultData)) {
          const records = res.data.ResultData;
          setPaymentHistory(records); // All records (parent + payments)
        }
      } catch (err) {
        console.error("Payment history fetch failed:", err);
      }
    };

    fetchPaymentHistory();
  }, [show, expense, userDataSession]);

  const newPaid = parseFloat(paidAmount) || 0;
  const calculatedDue = (currentDue - newPaid).toFixed(2);
  const isDueNegative = newPaid > currentDue;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    setImageFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (isFullyPaid) {
      setErrorMsg("This expense is already fully paid.");
      return;
    }

    if (isDueNegative) {
      setErrorMsg("Paid amount cannot exceed remaining due.");
      return;
    }

    if (!paidAmount || Number(paidAmount) <= 0) {
      setErrorMsg("Please enter a valid paid amount.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("Id", expense.Id);
    formData.append("RefId", expense.Id);
    formData.append("OrgId", userDataSession?.OrgId);
    formData.append("PaidAmount", paidAmount);
    formData.append("PaidDate", paidDate || "");
    formData.append("DueAmount", calculatedDue);
    formData.append("Note", notes.trim());
    formData.append("UpdatedBy", userDataSession?.UserId);
    formData.append("Status", Number(calculatedDue) <= 0 ? 1 : 0);

    // Optional: keep these if backend needs them, but not necessary
    // formData.append("StoreId", selectedStore?.value);
    // formData.append("SupplierId", selectedSupplier?.value);
    // formData.append("ExpenseTypeId", selectedExpenseType?.value);
    // formData.append("DateOfTransaction", billDate);
    // formData.append("Amount", billAmount);             // total – backend copies anyway
    // formData.append("ReferenceNo", referenceNo.trim());

    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      const res = await axios.post(
        `${API_BASE_URL}Misc/UpdateExpense`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (res.data?.message?.toLowerCase().includes("success")) {
        Swal.fire({
          title: "Success!",
          text:
            Number(calculatedDue) <= 0
              ? "Payment added – Expense is now fully cleared!"
              : "Additional payment added successfully!",
          icon: "success",
          timer: 1800,
          showConfirmButton: true,
        }).then(() => {
          onSuccess(); // refresh list
          onClose();
        });
      } else {
        throw new Error(res.data?.error || "Failed to add payment");
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Server error";
      setErrorMsg(msg);
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      {loading && <SpinLoader />}

      <style>{`
        .offcanvas { transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1); visibility: hidden; }
        .offcanvas.show { visibility: visible; }
        .offcanvas-backdrop { opacity: 0; transition: opacity 0.3s ease; }
        .offcanvas-backdrop.show { opacity: 0.5; }
        @media (max-width: 576px)    { #offcanvasEditExpense { width: 90% !important; } }
        @media (min-width: 577px) and (max-width: 992px)  { #offcanvasEditExpense { width: 55% !important; } }
        @media (min-width: 993px) and (max-width: 1440px) { #offcanvasEditExpense { width: 45% !important; } }
        @media (min-width: 1441px)   { #offcanvasEditExpense { width: 30% !important; } }
      `}</style>

      <div
        className={`offcanvas-backdrop fade ${show ? "show" : ""}`}
        style={{ pointerEvents: show ? "auto" : "none", zIndex: 1040 }}
        onClick={onClose}
      />

      <div
        className={`offcanvas offcanvas-end ${show ? "show" : ""}`}
        tabIndex="-1"
        id="offcanvasEditExpense"
        aria-labelledby="offcanvasEditExpenseLabel"
        style={{ pointerEvents: show ? "auto" : "none" }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasEditExpenseLabel">
            {isFullyPaid
              ? `Expense Fully Paid - ${referenceNo}`
              : `Add  Payment - ${referenceNo}`}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={onClose}
            aria-label="Close"
          />
        </div>
        <hr className="m-0 p-0" />

        <div className="offcanvas-body">
          {errorMsg && (
            <div
              className="alert alert-danger alert-dismissible  d-none"
              role="alert"
            >
              {errorMsg}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMsg("")}
              />
            </div>
          )}

          {isFullyPaid && (
            <div className="alert alert-info mb-3">
              This expense is already fully paid. No further payments can be
              added.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Store </label>
                <Select
                  value={selectedStore}
                  onChange={setSelectedStore}
                  options={stores}
                  placeholder="Select Store"
                  // isClearable
                  // isSearchable
                  isDisabled
                  // ={isFullyPaid}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Supplier </label>
                <Select
                  value={selectedSupplier}
                  onChange={setSelectedSupplier}
                  options={suppliers}
                  placeholder="Select Supplier"
                  // isClearable
                  // isSearchable
                  isDisabled
                  // ={isFullyPaid}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Expense Type </label>
                <Select
                  value={selectedExpenseType}
                  onChange={setSelectedExpenseType}
                  options={expenseTypes}
                  placeholder="Select Expense Type"
                  // isClearable
                  // isSearchable
                  isDisabled
                  // ={isFullyPaid}
                />
              </div>

              <div className="col-md-4 d-none">
                <label className="form-label">Ref. No</label>
                <input
                  type="text"
                  className="form-control"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  maxLength={50}
                  disabled={isFullyPaid}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Bill Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  disabled
                  // ={isFullyPaid}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Total Amount</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={billAmount}
                  readOnly
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Amount</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={paidAmount ? calculatedDue : currentDue.toFixed(2)}
                  readOnly
                />
                {isDueNegative && (
                  <small className="text-danger">
                    Paid amount exceeds remaining due
                  </small>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Paid Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  disabled={isFullyPaid}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Add Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  required
                  onWheel={(e) => e.target.blur()}
                  disabled={isFullyPaid}
                />
              </div>

              <div className="col-6">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={600}
                  disabled={isFullyPaid}
                />
                <small className="text-muted">{notes.length} / 600</small>
              </div>

                  <div className="col-6">
                <label className="form-label">Upload Receipt (optional)</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.pdf"
                  onChange={handleImageChange}
                  disabled={isFullyPaid}
                />
              </div>

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || isDueNegative || isFullyPaid}
                >
                  {loading ? "Adding..." : "Save"}
                </button>
              </div>
            </div>
          </form>

          {paymentHistory.length > 0 && (
            <div className="col-12 mt-4">
              <div className="accordion" id="paymentHistoryAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingPayments">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapsePayments"
                      aria-expanded="false"
                      aria-controls="collapsePayments"
                    >
                      Payment History ({paymentHistory.length} entries)
                    </button>
                  </h2>
                  <div
                    id="collapsePayments"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingPayments"
                    data-bs-parent="#paymentHistoryAccordion"
                  >
                    <div className="accordion-body p-0">
                      <div className="table-responsive">
                        <table className="table  table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Paid Amount</th>
                              <th>Paid Date</th>
                           
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paymentHistory.map((payment, index) => (
                              <tr key={payment.Id}>
                                <td>{index + 1}</td>
                                <td>
                                  ₹{Number(payment.PaidAmount || 0).toFixed(2)}
                                </td>
                                <td>
                                  {payment.PaidDate
                                    ? new Date(
                                        payment.PaidDate,
                                      ).toLocaleDateString("en-GB")
                                    : "-"}
                                </td>
                                
                                <td className="text-center">
                                  {payment.Note ? (
                                    <span title={payment.Note}>
                                      <i className="fa-regular fa-message text-primary"></i>
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

EditExpense.propTypes = {
  show: PropTypes.bool.isRequired,
  expense: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditExpense;
